<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>Nudge Template Manager</h1>
      <ol class="breadcrumb">
        <li><a href="#"><i class="fa fa-dashboard"></i> Home</a></li>
        <li class="active">AI Nudge Creation</li>
      </ol>
    </section>

    <section class="content">
      <div ref="pageBox" class="cc-page">
        <campaign-topbar
          title="AI Nudge Creation"
          subtitle="Create a WhatsApp template with AI: for one or more countries, in every language, with media and variables"
          show-help
          @new-chat="showNewChatDialog = true"
          @export="exportChat"
          @help="showHelp = true"
        />
        <campaign-steps-bar :current-step="currentStep" :steps="steps" />

        <div class="cc-body">
          <template-chat-panel :key="chatInstanceKey" ref="chatPanel" class="cc-chat" :resume-chat-id="resumeChatId" @summary-change="onSummaryChange" />
          <template-summary-sidebar class="cc-sidebar" :current-step="currentStep" :summary="summary" />
        </div>
      </div>

      <transition name="cc-overlay-fade">
        <div v-if="showNewChatDialog" class="cc-dialog-overlay" @click.self="showNewChatDialog = false">
          <div class="cc-dialog">
            <h4 class="cc-dialog-title">Start a new chat?</h4>
            <p class="cc-dialog-body">This draft will be closed and a fresh chat will begin. Anything already sent to WhatsApp stays sent.</p>
            <div class="cc-dialog-actions">
              <button class="cc-dialog-btn cc-dialog-btn-cancel" @click="showNewChatDialog = false">No</button>
              <button class="cc-dialog-btn cc-dialog-btn-confirm" @click="confirmNewChat">Yes</button>
            </div>
          </div>
        </div>
      </transition>

      <transition name="cc-overlay-fade">
        <div v-if="showHelp" class="cc-dialog-overlay" @click.self="showHelp = false">
          <div class="cc-dialog ct-help">
            <h4 class="cc-dialog-title">How AI Nudge Creation works</h4>
            <ol class="ct-help-list">
              <li><strong>Purpose:</strong> text, a link button, a call button, media, or a generic country template. Or copy an existing template to another WhatsApp account.</li>
              <li><strong>Where it lives:</strong> customers and countries. Each country's WhatsApp account is worked out for you; several countries can mean several accounts. A journey is optional and only tags the template.</li>
              <li><strong>Content:</strong> the AI drafts each language from your goal (up to 5 regenerations). Edit freely; variables like <code v-pre>{{1}}</code> need a sample for WhatsApp's review. Add an image, video or PDF from Media Management, or upload one.</li>
              <li><strong>Name &amp; checks:</strong> the name follows country_therapy-area_purpose_language_ID. WhatsApp's structural rules are checked on every language and account; the category and policy risk are advice.</li>
              <li><strong>Submit:</strong> each account × language is its own WhatsApp template. One failing doesn't stop the others, and you can retry just those. It's added to Nudge Template Manager automatically, and you'll get a notification for every decision.</li>
            </ol>
            <p class="cc-dialog-body">Type "change the countries", "change the message" and so on to go back to a step.</p>
            <div class="cc-dialog-actions">
              <button class="cc-dialog-btn cc-dialog-btn-confirm" @click="showHelp = false">Got it</button>
            </div>
          </div>
        </div>
      </transition>
    </section>
  </div>
</template>

<script>
import CampaignTopbar from './create-campaign/CampaignTopbar.vue'
import CampaignStepsBar from './create-campaign/CampaignStepsBar.vue'
import TemplateChatPanel from './create-template/TemplateChatPanel.vue'
import TemplateSummarySidebar from './create-template/TemplateSummarySidebar.vue'
import { TEMPLATE_STEP_LABELS } from './constants'
import { exportTranscript } from './chatExport'

// AI Nudge Creation (Nudge Template Manager menu): the standalone template chat, Templates
// PRD 3.3 / 3.4. Same page shell as Create Campaign.
export default {
  name: 'create-template',
  components: { CampaignTopbar, CampaignStepsBar, TemplateChatPanel, TemplateSummarySidebar },
  data () {
    return {
      steps: TEMPLATE_STEP_LABELS,
      showNewChatDialog: false,
      showHelp: false,
      chatInstanceKey: 0,
      currentStep: 1,
      summary: {}
    }
  },
  computed: {
    // Draft Chats links here with ?chatId= to resume that draft.
    resumeChatId () {
      return this.$route.query.chatId || null
    }
  },
  mounted () {
    this._setHeight()
    window.addEventListener('resize', this._setHeight)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this._setHeight)
  },
  methods: {
    _setHeight () {
      const el = this.$refs.pageBox
      if (!el) return
      el.style.height = (window.innerHeight - el.getBoundingClientRect().top - 10) + 'px'
    },
    onSummaryChange ({ step, summary }) {
      this.currentStep = step
      this.summary = summary
    },
    async confirmNewChat () {
      this.showNewChatDialog = false
      if (this.$refs.chatPanel && this.$refs.chatPanel.abandonChat) await this.$refs.chatPanel.abandonChat()
      if (this.$route.query.chatId) this.$router.replace({ query: {} })
      this.chatInstanceKey += 1
      this.currentStep = 1
      this.summary = {}
    },
    exportChat () {
      exportTranscript((this.$refs.chatPanel && this.$refs.chatPanel.messages) || [], 'Template Chat')
    }
  }
}
</script>

<style scoped src="./CreateCampaign.css"></style>
<style scoped>
.ct-help { max-width: 560px; text-align: left; }
.ct-help-list { padding-left: 18px; margin: 8px 0; font-size: 13px; color: #344054; }
.ct-help-list li { margin-bottom: 6px; }
</style>



// WEBPACK FOOTER //
// CreateTemplate.vue?4ff84f6a